import type { KeyConfig, KeyboardType } from "../../Keyboard/types";
import type {
  CommunityPreviewDeviceMeta,
  FullBoardConfigData,
} from "@eweadn-qiankun/shared-types";
import {
  isBeiyingDevice8815X98Keyboard,
  resolveXingshanMusicLayoutType,
} from "@eweadn-qiankun/shared-utils";
import {
  generateDefaultLayout,
  generateLayoutFromKeyCodeMapperMatrix,
} from "../../../utils/keyboardLayoutUtils";
import { applyChangeKeyRemaps } from "./changeKeyPreviewUtils";

/**
 * 社区预览矩阵类型：与改键页 useKeyboardLayout.resolveBeiyingVirtualKeyboardType 对齐。
 * 8815 X98 1K 须用 X98_1K，勿与 web-hid X98 8K 简表混用。
 */
export function resolveCommunityPreviewKeyboardType(
  meta: Pick<
    CommunityPreviewDeviceMeta,
    "keyboardType" | "solutionProvider" | "deviceCatalog"
  >
): string {
  const provider = meta.solutionProvider?.toLowerCase();
  if (provider === "beiying" || provider === "beiyingmachine") {
    if (isBeiyingDevice8815X98Keyboard(meta.deviceCatalog)) {
      return "X98_1K";
    }
    const fromCatalog = meta.deviceCatalog?.keyboardType?.trim();
    if (fromCatalog) return fromCatalog;
  }
  if (provider === "xingshan") {
    if (meta.deviceCatalog) {
      return resolveXingshanMusicLayoutType(meta.deviceCatalog);
    }
    const kt = meta.keyboardType?.trim();
    if (kt) return kt;
    return "87";
  }
  return meta.keyboardType?.trim() || "87";
}

/** 触发预览：按配置型号构造 CustomizeOperation 设备上下文 */
export function buildTriggerPreviewDeviceContext(
  meta: CommunityPreviewDeviceMeta,
  keyboardType: string
): {
  keyboardType: string;
  solutionRovider?: string;
  productId?: number;
  vendorId?: number;
  displayname?: string;
} {
  const catalog = meta.deviceCatalog;
  return {
    keyboardType,
    solutionRovider: meta.solutionProvider || catalog?.solutionRovider,
    productId: catalog?.productId,
    vendorId: catalog?.vendorId,
    displayname: meta.displayname,
  };
}

function isGapKey(key: KeyConfig): boolean {
  return key.code === "__gap__" || key.keyCode === 0;
}

/** 按物理键顺序重赋宽度，去掉矩阵占位列造成的右侧空键（与子应用 SDK 展示一致） */
function fixRowBySequentialWidths(
  row: KeyConfig[],
  widths: number[]
): KeyConfig[] {
  const keys = row.filter((k) => !isGapKey(k));
  return keys.map((key, index) => ({
    ...key,
    multipleWidth: widths[index] ?? key.multipleWidth,
  }));
}

/** getKeyCodeMatrix 与 KEYBOARD_LAYOUTS 不一致时，预览对齐子应用/贝盈 SDK */
const PREVIEW_USE_KEYCODE_MAPPER_LAYOUT = new Set(["m75"]);

/**
 * DK63 预览键宽：与 TOTAL_63 物理键顺序一致，勿改共享 LAYOUT_63 / 子应用矩阵。
 * Row3: Caps + 9 字母 + ; + ' + Enter
 * Row4: LShift + Z–M、,、. + /? + ↑ + RShift(宽)（与 TOTAL_63 固件列一致）
 * Row5: Ctrl Win Alt Space RAlt ← ↓ → Fn
 */
const DK63_PREVIEW_ROW_WIDTHS: Partial<Record<number, number[]>> = {
  3: [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.32],
  4: [2.15, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.9],
  5: [1.25, 1.25, 1.25, 6.62, 1, 1, 1, 1, 1],
};

function fixDk63PreviewLayout(layout: KeyConfig[][]): KeyConfig[][] {
  return layout.map((row, rowIndex) => {
    const widths = DK63_PREVIEW_ROW_WIDTHS[rowIndex];
    if (!widths?.length) return row;
    return fixRowBySequentialWidths(row, widths);
  });
}

function buildDk63PreviewLayout(): KeyConfig[][] {
  return fixDk63PreviewLayout(generateDefaultLayout("63"));
}

/**
 * 社区预览键盘矩阵：默认 generateDefaultLayout；
 * DK63 在相同紧凑矩阵基础上修正 Enter / Shift / 底行键宽，对齐子应用 SDK 展示。
 */
export function buildPreviewKeyboardLayout(
  metaOrKeyboardType: CommunityPreviewDeviceMeta | string
): KeyConfig[][] {
  const keyboardType =
    typeof metaOrKeyboardType === "string"
      ? metaOrKeyboardType
      : resolveCommunityPreviewKeyboardType(metaOrKeyboardType);
  if (keyboardType === "63") {
    return buildDk63PreviewLayout();
  }
  if (PREVIEW_USE_KEYCODE_MAPPER_LAYOUT.has(keyboardType)) {
    const fromMapper = generateLayoutFromKeyCodeMapperMatrix(
      keyboardType as KeyboardType
    );
    if (fromMapper) return fromMapper;
  }
  return generateDefaultLayout(keyboardType as KeyboardType);
}

/**
 * 社区预览键盘矩阵 + 改键快照。
 * 对齐子应用 useKeyboardLayout：Base 层 SDK 行矩阵 → convertLayoutRows；Fn 层走 convertLayoutRows 快照拼矩阵。
 */
export function buildPreviewKeyboardLayoutWithChangeKey(
  meta: CommunityPreviewDeviceMeta,
  configData?: FullBoardConfigData,
  currentDeviceSystem = 0,
  options?: { system?: "WIN" | "MAC"; layer?: number }
): KeyConfig[][] {
  const previewKeyboardType = resolveCommunityPreviewKeyboardType(meta);
  const baseLayout = buildPreviewKeyboardLayout(meta);
  const system = options?.system ?? (currentDeviceSystem === 1 ? "MAC" : "WIN");
  const layer = options?.layer ?? 0;
  let layout = applyChangeKeyRemaps(
    baseLayout,
    configData,
    system,
    layer,
    meta.solutionProvider,
    previewKeyboardType
  );
  if (previewKeyboardType === "63") {
    layout = fixDk63PreviewLayout(layout);
  }
  return layout;
}

/** @deprecated 兼容旧引用 */
export const buildRhythmPreviewKeyboardLayout = buildPreviewKeyboardLayout;
